import { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { calcBetLiability, calcBetProfit, normalizeBetLimits } from "./clientUtils";
import { loadStakeButtons } from "./clientStakeButtons";

const ClientBetSlip = ({
  selection,
  betType,
  odds,
  runLine = null,
  marketType = "bookmaker",
  minBet = 100,
  maxBet = 200000,
  balance = 0,
  placing = false,
  compact = false,
  marketLabel = "",
  onCancel,
  onPlace,
}) => {
  const { min, max } = useMemo(
    () => normalizeBetLimits(minBet, maxBet),
    [minBet, maxBet]
  );
  const bal = Number(balance) || 0;

  const [chips, setChips] = useState(() => loadStakeButtons("game"));
  // Empty until user types or taps a chip — never prefill 50/100
  const [stakeText, setStakeText] = useState("");
  const [oddsText, setOddsText] = useState(String(odds ?? ""));
  const [touched, setTouched] = useState(false);
  const slipIdRef = useRef("");

  useEffect(() => {
    const refresh = () => setChips(loadStakeButtons("game"));
    refresh();
    window.addEventListener("w777-buttons-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("w777-buttons-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  useEffect(() => {
    const slipId = `${selection}|${betType}|${odds}|${runLine ?? ""}`;
    if (slipIdRef.current === slipId) return;
    slipIdRef.current = slipId;
    setStakeText("");
    setOddsText(String(odds ?? ""));
    setTouched(false);
  }, [selection, betType, odds, runLine]);

  const stake = Number(stakeText);
  const stakeNum = Number.isFinite(stake) ? stake : 0;
  const oddsNum = Number(oddsText);
  const liveOdds = Number.isFinite(oddsNum) && oddsNum > 0 ? oddsNum : Number(odds) || 0;

  const stakeError = useMemo(() => {
    if (stakeText.trim() === "" || !Number.isFinite(stake) || stakeNum <= 0) {
      return "Amount select karo (+1k …)";
    }
    if (stakeNum < min) return `Minimum stake is ${min}`;
    if (stakeNum > max) return `Maximum stake is ${max}`;
    const hold = calcBetLiability(stakeNum, liveOdds || Number(odds) || 0, marketType, betType);
    // Never auto-reduce stake to balance — reject with insufficient balance.
    if (hold > bal) {
      return "Insufficient balance";
    }
    return "";
  }, [stakeText, stake, stakeNum, min, max, bal, liveOdds, odds, marketType, betType]);

  if (!selection) return null;

  const potentialWin =
    stakeNum && liveOdds ? calcBetProfit(stakeNum, liveOdds, marketType, betType) : "0.00";
  const liability =
    stakeNum && liveOdds ? calcBetLiability(stakeNum, liveOdds, marketType, betType) : 0;
  const modeLabel =
    marketType === "fancy"
      ? betType === "back"
        ? "YES"
        : "NO"
      : betType === "back"
        ? "LAGAI"
        : "KHAI";
  const canPlace = !stakeError && !placing && liveOdds > 0;

  // Cap only to market max — never silently clamp to wallet balance.
  const setStakeValue = (value) => {
    const n = Math.floor(Number(value) || 0);
    const next = Math.max(0, Math.min(n, max));
    setStakeText(next > 0 ? String(next) : "");
    setTouched(true);
  };

  const addStake = (amount) => {
    const next = (Number.isFinite(stakeNum) ? stakeNum : 0) + Number(amount);
    setStakeValue(next);
  };

  const clearAmount = () => {
    setStakeText("");
    setTouched(false);
  };

  const bumpOdds = (dir) => {
    const step = marketType === "match_odds" ? 0.01 : 1;
    const base = liveOdds || Number(odds) || 0;
    const next = Math.max(step, Math.round((base + dir * step) * 100) / 100);
    setOddsText(String(next));
  };

  const handlePlace = () => {
    setTouched(true);
    if (stakeError) {
      message.warning(stakeError);
      return;
    }
    onPlace(stakeNum, liveOdds);
  };

  return (
    <div className={`w777-bet-slip ${compact ? "inline" : ""} ${betType === "lay" ? "is-lay" : "is-back"}`}>
      <div className="w777-bet-slip-head">
        <span>Bet Slip</span>
        <button type="button" className="w777-bet-slip-x" onClick={onCancel} aria-label="Close">
          ×
        </button>
      </div>

      <div className="w777-bet-slip-body">
        <div className="w777-bet-slip-meta">
          <strong className="w777-bet-slip-match">{selection}</strong>
          <div className="w777-bet-slip-line">
            <span>{modeLabel}</span>
            <span>{(marketLabel || marketType || "MATCH_ODDS").toUpperCase()}</span>
            {marketType === "fancy" && runLine != null ? (
              <span>Runs {runLine}</span>
            ) : null}
          </div>
        </div>

        <div className={`w777-bet-slip-inputs ${touched && stakeError ? "has-error" : ""}`}>
          <input
            className="w777-bet-amount"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={stakeText}
            placeholder="Amount"
            onChange={(e) => {
              const raw = e.target.value.replace(/[^\d]/g, "");
              setTouched(true);
              setStakeText(raw);
            }}
            onBlur={() => setTouched(true)}
          />
          <div className="w777-bet-odds-wrap">
            <button type="button" className="w777-odds-step" onClick={() => bumpOdds(1)} aria-label="Odds up">
              ▲
            </button>
            <input
              className="w777-bet-odds"
              type="text"
              inputMode="decimal"
              value={oddsText}
              title={marketType === "fancy" ? "Rate" : "Odds"}
              onChange={(e) => setOddsText(e.target.value.replace(/[^\d.]/g, ""))}
            />
            <button type="button" className="w777-odds-step" onClick={() => bumpOdds(-1)} aria-label="Odds down">
              ▼
            </button>
          </div>
        </div>
        {touched && stakeError ? <div className="w777-bet-error">{stakeError}</div> : null}

        <div className="w777-bet-chips">
          {chips.map((chip) => (
            <button
              key={`${chip.label}-${chip.value}`}
              type="button"
              className="w777-bet-chip"
              onClick={() => addStake(chip.value)}>
              +{chip.label}
            </button>
          ))}
        </div>

        <div className="w777-bet-profit">
          {betType === "lay" ? (
            <>
              Liability <b>{Number(liability).toFixed(2)}</b>
              <span> · Profit <b>+{potentialWin}</b></span>
            </>
          ) : (
            <>
              Profit <b>+{potentialWin}</b>
              <span> · Rate {liveOdds}</span>
            </>
          )}
          {marketType === "fancy" && runLine != null ? (
            <span> · Line {runLine}</span>
          ) : null}
          <span>
            {" "}
            · Min {min} · Max {Math.floor(max)}
          </span>
        </div>

        <div className="w777-bet-actions">
          <button type="button" className="w777-bet-clear" onClick={clearAmount}>
            Clear
          </button>
          <button
            type="button"
            className="w777-bet-place"
            disabled={!canPlace}
            onClick={handlePlace}>
            {placing ? "…" : "Place Bet"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientBetSlip;
